import React, { useEffect, useMemo, useState } from 'react';
import {
  clearDeviceReadings,
  deleteDevice,
  getDevices,
  getSites,
  getStreamUsage,
  regenerateApiKey,
  updateDevice,
} from '../services/api';
import { copyToClipboard } from '../utils/copyToClipboard';
import { Device, Site, StreamUsage } from '../types';
import { useTimezone } from '../context/TimezoneContext';
import { useIsMobile } from '../hooks/useIsMobile';

type DeviceState = 'all' | 'online' | 'offline' | 'maintenance';

interface FieldMapping {
  rowId: string;
  field: string;
  label: string;
  path: string;
  type: 'number' | 'string';
  unit: string;
  scale: string;
  offset: string;
}

let mappingRowCounter = 0;

function createMappingRowId(prefix = 'mapping') {
  mappingRowCounter += 1;
  return `${prefix}-${mappingRowCounter}`;
}

function normalizeEditableNumber(value: string, fallback: number) {
  if (value.trim() === '') return fallback;
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function createEmptyMapping(): FieldMapping {
  return {
    rowId: createMappingRowId('new-mapping'),
    field: '',
    label: '',
    path: '',
    type: 'number',
    unit: '',
    scale: '1',
    offset: '0',
  };
}

export default function DevicesPage() {
  const { formatTime } = useTimezone();
  const isMobile = useIsMobile(860);
  const [devices, setDevices] = useState<Device[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [streamUsage, setStreamUsage] = useState<StreamUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DeviceState>('all');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    location: '',
    gateway_topic: '',
    device_identifier: '',
    site_id: '',
    area_id: '',
    maintenance_mode: false,
    heartbeat_timeout_minutes: '30',
    mappings: [] as FieldMapping[],
  });

  useEffect(() => {
    loadDevices();
    getSites().then(setSites).catch(() => {});
    // Older backends may not expose stream usage yet; fail silently and hide the card.
    getStreamUsage().then(setStreamUsage).catch(() => {});
  }, []);

  async function loadDevices() {
    try {
      const nextDevices = await getDevices();
      setDevices(nextDevices);
      setSelectedDevice((current) =>
        current ? nextDevices.find((device: Device) => device.id === current.id) || null : current
      );
    } catch {
      // Keep the last known fleet visible.
    }
  }

  async function handleRegenerateKey(deviceId: string) {
    if (!window.confirm('Regenerate API key? The old key will stop working immediately.')) return;
    setLoading(true);
    try {
      await regenerateApiKey(deviceId);
      await loadDevices();
    } catch {
      window.alert('Failed to regenerate key');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(deviceId: string, deviceName: string) {
    if (!window.confirm(`Delete "${deviceName}"? This cannot be undone.`)) return;
    setLoading(true);
    try {
      await deleteDevice(deviceId);
      if (selectedDevice?.id === deviceId) setSelectedDevice(null);
      await loadDevices();
    } catch {
      window.alert('Failed to delete device');
    } finally {
      setLoading(false);
    }
  }

  async function handleClearReadings(deviceId: string, deviceName: string) {
    if (!window.confirm(`Clear all readings for "${deviceName}"? The device itself will remain.`)) return;
    setLoading(true);
    try {
      await clearDeviceReadings(deviceId);
      await loadDevices();
    } catch {
      window.alert('Failed to clear readings');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleMaintenance(device: Device) {
    setLoading(true);
    try {
      await updateDevice(device.id, { maintenance_mode: !device.maintenance_mode });
      await loadDevices();
    } catch {
      window.alert('Failed to update maintenance mode');
    } finally {
      setLoading(false);
    }
  }

  async function copyKey(key: string, deviceId: string) {
    const ok = await copyToClipboard(key);
    if (!ok) {
      window.alert('Copy failed. Please copy the key manually.');
      return;
    }
    setCopiedId(deviceId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function openEdit(device: Device) {
    const mappings: FieldMapping[] = [];
    if (device.parser_config?.mappings) {
      for (const [key, mapping] of Object.entries(device.parser_config.mappings as Record<string, any>)) {
        mappings.push({
          rowId: createMappingRowId(`mapping-${key}`),
          field: key,
          label: mapping.label || '',
          path: mapping.path || '',
          type: mapping.type || 'number',
          unit: mapping.unit || '',
          scale: String(mapping.scale ?? 1),
          offset: String(mapping.offset ?? 0),
        });
      }
    }
    setEditForm({
      name: device.name || '',
      location: device.location || '',
      gateway_topic: device.gateway_topic || '',
      device_identifier: device.device_identifier || '',
      site_id: device.site_id || '',
      area_id: device.area_id || '',
      maintenance_mode: device.maintenance_mode === true,
      heartbeat_timeout_minutes: String(device.heartbeat_timeout_minutes ?? 30),
      mappings,
    });
    setEditingDevice(device);
  }

  async function saveEdit() {
    if (!editingDevice) return;
    setLoading(true);
    try {
      const parserConfig = {
        mappings: editForm.mappings.reduce((acc, mapping) => {
          if (mapping.field && mapping.path) {
            acc[mapping.field] = {
              path: mapping.path,
              scale: normalizeEditableNumber(mapping.scale, 1),
              offset: normalizeEditableNumber(mapping.offset, 0),
              unit: mapping.unit || undefined,
              label: mapping.label || undefined,
              type: mapping.type || 'number',
            };
          }
          return acc;
        }, {} as Record<string, any>),
      };

      await updateDevice(editingDevice.id, {
        name: editForm.name,
        location: editForm.location,
        heartbeat_timeout_minutes: normalizeEditableNumber(editForm.heartbeat_timeout_minutes, 30),
        gateway_topic: editForm.gateway_topic,
        device_identifier: editForm.device_identifier,
        parser_config: parserConfig,
        area_id: editForm.area_id || null,
        maintenance_mode: editForm.maintenance_mode,
      });
      setEditingDevice(null);
      await loadDevices();
    } catch {
      window.alert('Failed to update device');
    } finally {
      setLoading(false);
    }
  }

  function updateMapping(index: number, patch: Partial<FieldMapping>) {
    setEditForm((current) => {
      const nextMappings = [...current.mappings];
      if (!nextMappings[index]) return current;
      nextMappings[index] = { ...nextMappings[index], ...patch };
      return { ...current, mappings: nextMappings };
    });
  }

  const filteredDevices = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return devices.filter((device) => {
      const state = getDeviceState(device);
      const matchesStatus = statusFilter === 'all' || state === statusFilter;
      const haystack = [
        device.name,
        device.location,
        device.topic,
        device.site_name,
        device.area_name,
        device.gateway_topic,
        device.device_identifier,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return matchesStatus && (!needle || haystack.includes(needle));
    });
  }, [devices, search, statusFilter]);

  const counts = {
    total: devices.length,
    online: devices.filter((device) => getDeviceState(device) === 'online').length,
    maintenance: devices.filter((device) => getDeviceState(device) === 'maintenance').length,
    offline: devices.filter((device) => getDeviceState(device) === 'offline').length,
  };
  const streamLimitReached = streamUsage != null && streamUsage.limit != null && streamUsage.total >= streamUsage.limit;
  const streamCountByDeviceId = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of streamUsage?.devices || []) {
      map.set(entry.device_id, entry.stream_count);
    }
    return map;
  }, [streamUsage]);
  const selected = selectedDevice || filteredDevices[0] || null;

  return (
    <div className="sd-devices-page">
      <section className="sd-page-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">MANAGE</div>
          <h1>Device Fleet</h1>
          <p>Registered assets, MQTT topics, parser mappings, heartbeat status, and credentials.</p>
        </div>
      </section>

      <section className="sd-map-stat-grid">
        <FleetStat label="Total devices" value={counts.total} detail="Registered assets" tone="blue" />
        {streamUsage && (
          <FleetStat
            label="Data streams"
            value={streamUsage.total}
            detail={
              streamLimitReached
                ? 'Plan limit reached — upgrade for more'
                : streamUsage.limit == null
                  ? 'Unlimited on current plan'
                  : `${streamUsage.total} of ${streamUsage.limit} used on current plan`
            }
            tone={streamLimitReached ? 'danger' : 'blue'}
          />
        )}
        <FleetStat label="Online" value={counts.online} detail="Healthy heartbeat" tone="success" />
        <FleetStat label="Maintenance" value={counts.maintenance} detail="Alert suppression" tone="warning" />
        <FleetStat label="Offline" value={counts.offline} detail="Needs attention" tone="danger" />
      </section>

      <section className="sd-devices-workspace">
        <article className="sd-panel sd-devices-table-panel">
          <div className="sd-panel-header sd-devices-toolbar">
            <div>
              <h2>Asset registry</h2>
              <p>{filteredDevices.length} of {devices.length} devices shown</p>
            </div>
            <div className="sd-device-tools">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search devices, sites, topics..."
              />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as DeviceState)}>
                <option value="all">All status</option>
                <option value="online">Online</option>
                <option value="maintenance">Maintenance</option>
                <option value="offline">Offline</option>
              </select>
            </div>
          </div>

          {isMobile ? (
            <div className="sd-device-card-list">
              {filteredDevices.map((device) => (
                <button
                  key={device.id}
                  className={`sd-device-row-card ${selected?.id === device.id ? 'is-selected' : ''}`}
                  onClick={() => setSelectedDevice(device)}
                  type="button"
                >
                  <span className={`sd-map-status-dot is-${getDeviceState(device)}`} />
                  <div>
                    <strong>{device.name}</strong>
                    <span>{([device.site_name, device.area_name].filter(Boolean).join(' / ') || device.location || 'Unassigned') + streamCountMeta(streamCountByDeviceId.get(device.id))}</span>
                    <code>{device.topic}</code>
                  </div>
                  <StatusLabel device={device} />
                </button>
              ))}
              {filteredDevices.length === 0 && <EmptyState />}
            </div>
          ) : (
            <div className="sd-table-wrap">
              <table className="sd-table sd-device-registry-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Device</th>
                    <th>Site / Area</th>
                    <th>MQTT Username</th>
                    <th>Telemetry</th>
                    <th>Last seen</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <EmptyState />
                      </td>
                    </tr>
                  )}
                  {filteredDevices.map((device) => (
                    <tr
                      key={device.id}
                      className={selected?.id === device.id ? 'is-selected' : ''}
                      onClick={() => setSelectedDevice(device)}
                    >
                      <td>
                        <StatusLabel device={device} />
                      </td>
                      <td>
                        <strong>{device.name}</strong>
                        <span>{(device.location || 'No location set') + streamCountMeta(streamCountByDeviceId.get(device.id))}</span>
                      </td>
                      <td>{[device.site_name, device.area_name].filter(Boolean).join(' / ') || 'Unassigned'}</td>
                      <td className="sd-mono">{device.topic}</td>
                      <td>
                        <TelemetryInline device={device} />
                      </td>
                      <td>{device.last_reading_at ? formatTime(device.last_reading_at) : 'No readings'}</td>
                      <td>
                        <div className="sd-row-actions">
                          <button onClick={(e) => { e.stopPropagation(); openEdit(device); }} type="button">
                            Edit
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleToggleMaintenance(device); }} disabled={loading} type="button">
                            {device.maintenance_mode ? 'Maint off' : 'Maint on'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>

        <DeviceInspector
          copiedId={copiedId}
          copyKey={copyKey}
          device={selected}
          handleClearReadings={handleClearReadings}
          handleDelete={handleDelete}
          handleRegenerateKey={handleRegenerateKey}
          handleToggleMaintenance={handleToggleMaintenance}
          loading={loading}
          openEdit={openEdit}
        />
      </section>

      {editingDevice && (
        <EditDeviceDrawer
          editForm={editForm}
          editingDevice={editingDevice}
          loading={loading}
          saveEdit={saveEdit}
          setEditForm={setEditForm}
          setEditingDevice={setEditingDevice}
          sites={sites}
          updateMapping={updateMapping}
        />
      )}
    </div>
  );
}

function FleetStat({
  detail,
  label,
  tone,
  value,
}: {
  detail: string;
  label: string;
  tone: 'blue' | 'danger' | 'success' | 'warning';
  value: number;
}) {
  return (
    <article className={`sd-map-stat is-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function DeviceInspector({
  copiedId,
  copyKey,
  device,
  handleClearReadings,
  handleDelete,
  handleRegenerateKey,
  handleToggleMaintenance,
  loading,
  openEdit,
}: {
  copiedId: string | null;
  copyKey: (key: string, deviceId: string) => void;
  device: Device | null;
  handleClearReadings: (deviceId: string, deviceName: string) => void;
  handleDelete: (deviceId: string, deviceName: string) => void;
  handleRegenerateKey: (deviceId: string) => void;
  handleToggleMaintenance: (device: Device) => void;
  loading: boolean;
  openEdit: (device: Device) => void;
}) {
  if (!device) {
    return (
      <aside className="sd-panel sd-device-inspector">
        <div className="sd-panel-header">
          <div>
            <h2>Inspector</h2>
            <p>Select a device to view details.</p>
          </div>
        </div>
        <div className="sd-inspector-empty">
          <b>--</b>
          <span>No device selected</span>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sd-panel sd-device-inspector">
      <div className="sd-panel-header">
        <div>
          <h2>Inspector</h2>
          <p>Credentials, status, and mappings</p>
        </div>
      </div>

      <div className="sd-device-inspector-body">
        <div className="sd-inspector-title">
          <span className={`sd-map-status-dot is-${getDeviceState(device)}`} />
          <div>
            <h3>{device.name}</h3>
            <p>{device.id.slice(0, 8)}</p>
          </div>
        </div>
        <StatusLabel device={device} />

        <div className="sd-inspector-facts">
          <FactRow label="Site" value={device.site_name || 'Unassigned'} />
          <FactRow label="Area" value={device.area_name || 'Unassigned'} />
          <FactRow label="Location" value={device.location || 'No location'} />
          <FactRow label="Heartbeat" value={`${device.heartbeat_timeout_minutes ?? '--'} min`} />
          <FactRow label="Last seen" value={device.last_reading_at ? new Date(device.last_reading_at).toLocaleString() : 'No readings'} />
          <FactRow label="MQTT Username" value={device.topic || 'No topic'} mono />
          {device.gateway_topic && <FactRow label="Publish Topic (gateway)" value={device.gateway_topic} mono />}
          {device.device_identifier && <FactRow label="Identifier" value={device.device_identifier} mono />}
        </div>

        <div className="sd-inspector-section-title">Credentials</div>
        <div className="sd-key-card">
          <code>{device.api_key ? maskKey(device.api_key) : 'Virtual device or key unavailable'}</code>
          {device.api_key && (
            <button onClick={() => copyKey(device.api_key!, device.id)} type="button">
              {copiedId === device.id ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        <div className="sd-inspector-section-title">Telemetry health</div>
        <div className="sd-inspector-metrics">
          <MetricTile label="Battery" value={device.latest_battery} unit="%" />
          <MetricTile label="Signal" value={device.latest_signal_strength} unit="dBm" />
          <MetricTile label="Parser" value={device.parser_type || (device.mqtt_connection_id ? 'virtual' : 'direct')} type="string" unit="" />
        </div>

        {device.parser_config?.mappings && Object.keys(device.parser_config.mappings).length > 0 && (
          <>
            <div className="sd-inspector-section-title">Field mappings</div>
            <div className="sd-mapping-list">
              {Object.entries(device.parser_config.mappings).map(([key, mapping]: [string, any]) => (
                <div key={key}>
                  <strong>{mapping.label || key.replace(/_/g, ' ')}</strong>
                  <code>{mapping.path || key}</code>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="sd-device-quick-actions">
          <button onClick={() => openEdit(device)} disabled={loading} type="button">
            Edit device
          </button>
          <button onClick={() => handleToggleMaintenance(device)} disabled={loading} type="button">
            {device.maintenance_mode ? 'Disable maintenance' : 'Maintenance mode'}
          </button>
          {!device.mqtt_connection_id && (
            <button onClick={() => handleRegenerateKey(device.id)} disabled={loading} type="button">
              Regenerate key
            </button>
          )}
          <button onClick={() => handleClearReadings(device.id, device.name)} disabled={loading} type="button">
            Clear readings
          </button>
          <button className="is-danger" onClick={() => handleDelete(device.id, device.name)} disabled={loading} type="button">
            Delete
          </button>
        </div>
      </div>
    </aside>
  );
}

function EditDeviceDrawer({
  editForm,
  editingDevice,
  loading,
  saveEdit,
  setEditForm,
  setEditingDevice,
  sites,
  updateMapping,
}: {
  editForm: {
    name: string;
    location: string;
    heartbeat_timeout_minutes: string;
    gateway_topic: string;
    device_identifier: string;
    site_id: string;
    area_id: string;
    maintenance_mode: boolean;
    mappings: FieldMapping[];
  };
  editingDevice: Device;
  loading: boolean;
  saveEdit: () => void;
  setEditForm: React.Dispatch<React.SetStateAction<{
    name: string;
    location: string;
    heartbeat_timeout_minutes: string;
    gateway_topic: string;
    device_identifier: string;
    site_id: string;
    area_id: string;
    maintenance_mode: boolean;
    mappings: FieldMapping[];
  }>>;
  setEditingDevice: React.Dispatch<React.SetStateAction<Device | null>>;
  sites: Site[];
  updateMapping: (index: number, patch: Partial<FieldMapping>) => void;
}) {
  const availableAreas = sites.find((site) => site.id === editForm.site_id)?.areas || [];

  return (
    <div className="sd-drawer-overlay" onClick={() => setEditingDevice(null)}>
      <aside className="sd-edit-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="sd-edit-drawer-header">
          <div>
            <span>Device editor</span>
            <h2>{editingDevice.name}</h2>
          </div>
          <button onClick={() => setEditingDevice(null)} type="button">Close</button>
        </div>

        <div className="sd-form-grid">
          <Field label="Name" hint="Human-readable asset name shown across dashboards, reports, and alerts.">
            <input
              placeholder="e.g. Wellhead Pressure PT-101"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
          </Field>
          <Field label="Location" hint="Optional physical label for operators, such as a room, skid, bay, or area note.">
            <input
              placeholder="e.g. Pump House, Bay 2"
              value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
            />
          </Field>
          <Field label="Site" hint="Choose the facility that owns this asset. Leave unassigned if not yet placed.">
            <select
              value={editForm.site_id}
              onChange={(e) => {
                const siteId = e.target.value;
                const areas = sites.find((site) => site.id === siteId)?.areas || [];
                setEditForm({
                  ...editForm,
                  site_id: siteId,
                  area_id: areas.some((area) => area.id === editForm.area_id) ? editForm.area_id : '',
                });
              }}
            >
              <option value="">Unassigned</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Area" hint="Choose the operational zone or floor-plan area inside the selected site.">
            <select
              value={editForm.area_id}
              onChange={(e) => setEditForm({ ...editForm, area_id: e.target.value })}
              disabled={!editForm.site_id}
            >
              <option value="">Unassigned</option>
              {availableAreas.map((area) => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Heartbeat minutes" hint="Minutes without telemetry before this device is considered offline. Blank saves as 30.">
            <input
              placeholder="e.g. 30"
              type="number"
              value={editForm.heartbeat_timeout_minutes}
              onChange={(e) => setEditForm({ ...editForm, heartbeat_timeout_minutes: e.target.value })}
            />
          </Field>
          <Field label="Gateway topic" hint="For virtual/gateway devices, enter the shared MQTT topic carrying this asset's payload.">
            <input
              placeholder="e.g. plant/gateway-01/readings"
              value={editForm.gateway_topic}
              onChange={(e) => setEditForm({ ...editForm, gateway_topic: e.target.value })}
            />
          </Field>
          <Field label="Device identifier" hint="Identifier inside gateway payloads, such as a serial number or device key.">
            <input
              placeholder="e.g. VI_9207 or 493C250702069207"
              value={editForm.device_identifier}
              onChange={(e) => setEditForm({ ...editForm, device_identifier: e.target.value })}
            />
          </Field>
        </div>

        <label className="sd-maintenance-toggle">
          <input
            checked={editForm.maintenance_mode}
            onChange={(e) => setEditForm({ ...editForm, maintenance_mode: e.target.checked })}
            type="checkbox"
          />
          <span>
            <strong>Maintenance mode</strong>
            Suppress offline alerting and mark this asset as planned maintenance.
          </span>
        </label>

        <div className="sd-inspector-section-title">Field mappings</div>
        <div className="sd-edit-mapping-stack">
          {editForm.mappings.map((mapping, index) => (
            <div className="sd-edit-mapping-card" key={mapping.rowId}>
              <div className="sd-form-grid">
                <Field label="Field key" hint="Internal metric key used by charts and alerts. Use short snake_case names.">
                  <input
                    placeholder="e.g. fuel_level"
                    value={mapping.field}
                    onChange={(e) => updateMapping(index, { field: e.target.value })}
                  />
                </Field>
                <Field label="Display label" hint="Friendly label operators see in dashboards. Leave blank to use the field key.">
                  <input
                    placeholder="e.g. Fuel Level"
                    value={mapping.label}
                    onChange={(e) => updateMapping(index, { label: e.target.value })}
                  />
                </Field>
                <Field label="Type" hint="Use Number for telemetry values, String for states like ON, OFF, RUNNING, or FAULT.">
                  <select value={mapping.type} onChange={(e) => updateMapping(index, { type: e.target.value as 'number' | 'string' })}>
                    <option value="number">Number</option>
                    <option value="string">String</option>
                  </select>
                </Field>
                <Field label="JSON path" hint="Path to the value inside incoming payloads. Dot paths and $. paths are supported.">
                  <input
                    placeholder="e.g. $.tank.level or data.pressure"
                    value={mapping.path}
                    onChange={(e) => updateMapping(index, { path: e.target.value })}
                  />
                </Field>
                <Field label="Scale" hint="Multiplier applied before saving the value. Blank defaults to 1.">
                  <input
                    disabled={mapping.type === 'string'}
                    placeholder="e.g. 1"
                    step="any"
                    type="number"
                    value={mapping.scale}
                    onChange={(e) => updateMapping(index, { scale: e.target.value })}
                  />
                </Field>
                <Field label="Offset" hint="Value added after scale. Use 0 when no calibration offset is needed.">
                  <input
                    disabled={mapping.type === 'string'}
                    placeholder="e.g. 0"
                    step="any"
                    type="number"
                    value={mapping.offset}
                    onChange={(e) => updateMapping(index, { offset: e.target.value })}
                  />
                </Field>
                <Field label="Unit" hint="Optional engineering unit shown beside readings and chart values.">
                  <input
                    placeholder="e.g. bar, L, %, kWh"
                    value={mapping.unit}
                    onChange={(e) => updateMapping(index, { unit: e.target.value })}
                  />
                </Field>
              </div>
              <button
                className="sd-remove-mapping"
                onClick={() =>
                  setEditForm((current) => ({
                    ...current,
                    mappings: current.mappings.filter((_, mappingIndex) => mappingIndex !== index),
                  }))
                }
                type="button"
              >
                Remove mapping
              </button>
            </div>
          ))}
          {editForm.mappings.length === 0 && (
            <div className="sd-empty-row">No field mappings configured.</div>
          )}
          <button
            className="sd-link-button"
            onClick={() =>
              setEditForm((current) => ({
                ...current,
                mappings: [...current.mappings, createEmptyMapping()],
              }))
            }
            type="button"
          >
            Add field mapping
          </button>
        </div>

        <div className="sd-drawer-actions">
          <button onClick={() => setEditingDevice(null)} type="button">Cancel</button>
          <button onClick={saveEdit} disabled={loading} type="button">
            {loading ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </aside>
    </div>
  );
}

function Field({ children, hint, label }: { children: React.ReactNode; hint?: string; label: string }) {
  return (
    <label className="sd-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}

function StatusLabel({ device }: { device: Device }) {
  const state = getDeviceState(device);
  return (
    <span className={`sd-status-label is-${state}`}>
      <i />
      {state === 'maintenance' ? 'Maintenance' : state === 'online' ? 'Online' : 'Offline'}
    </span>
  );
}

function TelemetryInline({ device }: { device: Device }) {
  const parts = [
    device.latest_battery != null ? `Battery ${device.latest_battery.toFixed(0)}%` : null,
    device.latest_signal_strength != null ? `Signal ${device.latest_signal_strength.toFixed(0)} dBm` : null,
    device.parser_type ? `Parser ${device.parser_type}` : device.mqtt_connection_id ? 'Virtual' : 'Direct',
  ].filter(Boolean);
  return <span>{parts.join(' / ') || 'No telemetry summary'}</span>;
}

function MetricTile({
  label,
  type = 'number',
  unit,
  value,
}: {
  label: string;
  type?: 'number' | 'string';
  unit: string;
  value?: any;
}) {
  let display = '--';
  if (value !== undefined && value !== null && value !== '') {
    display = type === 'number' && typeof value === 'number'
      ? `${value.toFixed(unit === '%' ? 0 : 1)}${unit ? ` ${unit}` : ''}`
      : String(value);
  }

  return (
    <div className="sd-metric-tile">
      <span>{label}</span>
      <strong>{display}</strong>
    </div>
  );
}

function FactRow({ label, mono, value }: { label: string; mono?: boolean; value: string }) {
  return (
    <div className="sd-fact-row">
      <span>{label}</span>
      <strong className={mono ? 'sd-mono' : undefined}>{value}</strong>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="sd-devices-empty">
      <strong>No devices match this view.</strong>
      <span>Try clearing search or changing the status filter.</span>
    </div>
  );
}

function getDeviceState(device: Device): Exclude<DeviceState, 'all'> {
  if (device.maintenance_mode) return 'maintenance';
  return device.status === 'online' ? 'online' : 'offline';
}

function streamCountMeta(count?: number) {
  return count == null ? '' : ` · ${count} stream${count === 1 ? '' : 's'}`;
}

function maskKey(key: string) {
  if (key.length <= 20) return key;
  return `${key.slice(0, 12)}...${key.slice(-6)}`;
}
